import React, { useEffect } from 'react';
import { Field } from 'formik';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CustomAlert, CustomSelect, PasswordField } from 'components/shared';
import { UserFormAction } from 'pages/user/user_form';
import { set } from 'lodash';
import { useAuth } from 'contexts/auth/auth_context';

interface LoginDataFormProps {
  groupOptions: { data: { uuid: string; name: string }[] };
  profileOptions: { data: { uuid: string; name: string }[] };
  permissionOptions: { data: { uuid: string; name: string }[] };
  institutionOptions: { data: { uuid: string; name: string }[] };
  values: any;
  errors: any;
  touched: any;
  setFieldValue: (field: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
  userFormAction: UserFormAction;
  userPermission?: string;
  permissionViewPassword?: boolean;
}

enum UserPermission {
  ADMINISTRADOR = 'ADMINISTRADOR',
  REPRESENTANTE_LEGAL_ESTADUAL = 'REPRESENTANTE LEGAL ESTADUAL',
  GERENTE_ESTADUAL = 'GERENTE ESTADUAL',
  REPRESENTANTE_LEGAL_MUNICIPAL = 'REPRESENTANTE LEGAL MUNICIPAL',
  GERENTE_MUNICIPAL = 'GERENTE MUNICIPAL',
  OPERADOR_MUNICIPAL = 'OPERADOR MUNICIPAL',
  CONSELHEIRO_MUNICIPAL = 'CONSELHEIRO MUNICIPAL',
  CONSELHEIRO_ESTADUAL = 'CONSELHEIRO ESTADUAL',
  OPERADOR_ESTADUAL = 'OPERADOR ESTADUAL',
}

const LoginDataForm: React.FC<LoginDataFormProps> = ({
  groupOptions,
  profileOptions,
  permissionOptions,
  setFieldValue,
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  userPermission,
  userFormAction,
  permissionViewPassword = false,
}) => {
  const getErrorMessage = () => {
    if (errors.loginData?.group && touched.loginData?.group) {
      return 'Selecione a opção que deseja do "Grupo do Perfil" abaixo para prosseguir.';
    }
    if (errors.loginData?.profile && touched.loginData?.profile) {
      return 'Selecione a opção que deseja do "Perfil do Usuário" abaixo para prosseguir.';
    }
    if (errors.loginData?.permission && touched.loginData?.permission) {
      return 'Selecione a opção que deseja da "Permissão do Usuário" abaixo para prosseguir.';
    }
    if (errors.loginData?.password && touched.loginData?.password) {
      return 'Preencha o campo "Senha de assinatura digital" corretamente e com um dado válido.';
    }
    return null;
  };

  const errorMessage = getErrorMessage();

  const { user } = useAuth();

  const listBlockMunicipal = [
    UserPermission.GERENTE_MUNICIPAL,
    UserPermission.REPRESENTANTE_LEGAL_MUNICIPAL,
  ];

  const isProfileDisabled = () => {
    const permissionUser = permissionOptions.data.find(
      (option) => option.name === userPermission,
    )?.name;
    const permissionName = permissionUser!;

    if (
      userPermission === UserPermission.ADMINISTRADOR ||
      userPermission === UserPermission.REPRESENTANTE_LEGAL_ESTADUAL ||
      userPermission === UserPermission.GERENTE_ESTADUAL ||
      userPermission === UserPermission.REPRESENTANTE_LEGAL_MUNICIPAL ||
      userPermission === UserPermission.GERENTE_MUNICIPAL
    ) {
      return false;
    }

    if (userPermission === 'GERENTE ESTADUAL') {
      const listaNaoPermitida = [
        'GERENTE ESTADUAL',
        'OPERADOR ESTADUAL',
        'CONSELHEIRO ESTADUAL',
      ];
      if (listaNaoPermitida.includes(permissionName)) {
        return true;
      }
    }

    if (userPermission === 'OPERADOR ESTADUAL') {
      const listaNaoPermitida = [
        'GERENTE ESTADUAL',
        'OPERADOR ESTADUAL',
        'CONSELHEIRO ESTADUAL',
      ];
      if (listaNaoPermitida.includes(permissionName)) {
        return true;
      }
    }

    if (userPermission === 'CONSELHEIRO ESTADUAL') {
      const listaNaoPermitida = [
        'GERENTE ESTADUAL',
        'OPERADOR ESTADUAL',
        'CONSELHEIRO ESTADUAL',
      ];
      if (listaNaoPermitida.includes(permissionName)) {
        return true;
      }
    }

    if (userPermission === 'REPRESENTANTE LEGAL MUNICIPAL') {
      if (permissionName === 'REPRESENTANTE LEGAL MUNICIPAL') {
        return true;
      }
    }

    if (userPermission === 'GERENTE MUNICIPAL') {
      const listaNaoPermitida = [
        'GERENTE MUNICIPAL',
        'REPRESENTANTE LEGAL MUNICIPAL',
      ];
      if (listaNaoPermitida.includes(permissionName)) {
        return true;
      }
    }

    if (userPermission === 'OPERADOR MUNICIPAL') {
      return true;
    }

    if (userPermission === 'CONSELHEIRO MUNICIPAL') {
      return true;
    }

    return false;
  };

  const isDisabled = isProfileDisabled();

  const notEditPermission = (permission: string) => {
    const notEditPermissions = [
      UserPermission.CONSELHEIRO_MUNICIPAL,
      UserPermission.OPERADOR_MUNICIPAL,
      UserPermission.CONSELHEIRO_ESTADUAL,
      UserPermission.OPERADOR_ESTADUAL,
    ];

    return notEditPermissions.includes(permission as UserPermission);
  };

  const isNotEditPermission = notEditPermission(user?.permission.name as any);

  useEffect(() => {
    if (!user) return;

    if (
      user?.permission.name === UserPermission.GERENTE_MUNICIPAL ||
      user?.permission.name === UserPermission.REPRESENTANTE_LEGAL_MUNICIPAL
    ) {
      const groupMunicipio = groupOptions.data.find(
        (group) => group.name === 'MUNICIPAL',
      );

      setFieldValue('loginData.group', groupMunicipio?.uuid);
    }
  }, [groupOptions, user]);

  return (
    <Box sx={{ width: '1152px', marginTop: '20px' }}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant='h6'>Dados de login</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {errorMessage && (
            <CustomAlert severity='error' message={errorMessage} />
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
              <FormControl size='small' fullWidth>
                <InputLabel
                  sx={{
                    fontFamily: 'Roboto',
                    fontSize: '12px',
                    fontWeight: 400,
                    letterSpacing: '0.15px',
                    textAlign: 'left',
                    color: '#00000099',
                    '&.Mui-focused': {
                      color: '#00000099',
                    },
                  }}
                >
                  Grupo do Perfil
                </InputLabel>
                <Field
                  name='loginData.group'
                  as={CustomSelect}
                  labelId='group-label'
                  id='group-select'
                  label='Grupo do Perfil'
                  size='small'
                  value={values.loginData.group}
                  disabled={
                    (user &&
                      listBlockMunicipal.includes(userPermission as any)) ||
                    isNotEditPermission
                  }
                  onChange={(e: any) => {
                    const selectedValue = e.target.value;
                    setFieldValue('loginData.group', selectedValue);
                    setFieldValue('loginData.profile', '');
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  error={
                    touched.loginData?.group && Boolean(errors.loginData?.group)
                  }
                >
                  {groupOptions?.data?.map((option) => {
                    return (
                      <MenuItem
                        key={option.uuid}
                        value={option.uuid}
                        sx={{
                          fontFamily: 'Roboto',
                          fontSize: '16px',
                          fontWeight: 400,
                          lineHeight: '24px',
                          letterSpacing: '0.15px',
                          textAlign: 'left',
                        }}
                      >
                        {option.name}
                      </MenuItem>
                    );
                  })}
                </Field>

                {touched.loginData?.group && errors.loginData?.group && (
                  <Typography
                    color='error'
                    variant='caption'
                    textAlign={'left'}
                  >
                    {errors.loginData?.group}
                  </Typography>
                )}
              </FormControl>

              <FormControl size='small' fullWidth>
                <InputLabel
                  sx={{
                    fontFamily: 'Roboto',
                    fontSize: '12px',
                    fontWeight: 400,
                    letterSpacing: '0.15px',
                    textAlign: 'left',
                    color: '#00000099',
                    '&.Mui-focused': {
                      color: '#00000099',
                    },
                  }}
                >
                  Perfil do Usuário
                </InputLabel>
                <Field
                  name='loginData.profile'
                  as={CustomSelect}
                  labelId='profile-label'
                  id='profile-select'
                  label='Perfil do Usuário'
                  size='small'
                  value={values.loginData.profile}
                  disabled={isDisabled || isNotEditPermission}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.loginData?.profile &&
                    Boolean(errors.loginData?.profile)
                  }
                >
                  {profileOptions?.data?.map((option) => {
                    const isMunicipal =
                      user?.permission.name ===
                        UserPermission.GERENTE_MUNICIPAL ||
                      user?.permission.name ===
                        UserPermission.REPRESENTANTE_LEGAL_MUNICIPAL;

                    if (isMunicipal) {
                      if (
                        option.name.toUpperCase().includes('ESTADUAL') ||
                        option.name.toUpperCase().includes('ADMINISTRADOR') ||
                        option.name.toUpperCase().includes('ESTADO')
                      ) {
                        return null;
                      }
                    }

                    return (
                      <MenuItem
                        key={option.uuid}
                        value={option.uuid}
                        sx={{
                          fontFamily: 'Roboto',
                          fontSize: '16px',
                          fontWeight: 400,
                          lineHeight: '24px',
                          letterSpacing: '0.15px',
                          textAlign: 'left',
                        }}
                      >
                        {option.name}
                      </MenuItem>
                    );
                  })}
                </Field>

                {touched.loginData?.profile && errors.loginData?.profile && (
                  <Typography
                    color='error'
                    variant='caption'
                    textAlign={'left'}
                  >
                    {errors.loginData?.profile}
                  </Typography>
                )}
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
              <FormControl size='small' fullWidth>
                <InputLabel
                  sx={{
                    fontFamily: 'Roboto',
                    fontSize: '12px',
                    fontWeight: 400,
                    letterSpacing: '0.15px',
                    textAlign: 'left',
                    color: '#00000099',
                    '&.Mui-focused': {
                      color: '#00000099',
                    },
                  }}
                >
                  Permissão do Usuário
                </InputLabel>
                <Field
                  name='loginData.permission'
                  as={CustomSelect}
                  labelId='permission-label'
                  id='permission-select'
                  label='Permissão do Usuário'
                  size='small'
                  value={values.loginData.permission}
                  onChange={handleChange}
                  disabled={isDisabled || isNotEditPermission}
                  onBlur={handleBlur}
                  error={
                    touched.loginData?.permission &&
                    Boolean(errors.loginData?.permission)
                  }
                >
                  {permissionOptions?.data.map((option) => {
                    const isMunicipal =
                      user?.permission.name ===
                        UserPermission.GERENTE_MUNICIPAL ||
                      user?.permission.name ===
                        UserPermission.REPRESENTANTE_LEGAL_MUNICIPAL;

                    if (isMunicipal) {
                      if (
                        option.name.toUpperCase().includes('ESTADUAL') ||
                        option.name.toUpperCase().includes('ADMINISTRADOR') ||
                        option.name.toUpperCase().includes('ESTADO') ||
                        option.name
                          .toUpperCase()
                          .includes('REPRESENTANTE LEGAL ESTADUAL')
                      ) {
                        return null;
                      }

                      if (userFormAction === UserFormAction.CADASTRAR) {
                        if (
                          option.name
                            .toUpperCase()
                            .includes('REPRESENTANTE LEGAL MUNICIPAL')
                        ) {
                          return null;
                        }
                      }

                      if (
                        option.name
                          .toUpperCase()
                          .includes(
                            UserPermission.REPRESENTANTE_LEGAL_ESTADUAL,
                          ) &&
                        user?.permission.name ===
                          UserPermission.GERENTE_MUNICIPAL
                      ) {
                        return null;
                      }
                    }

                    return (
                      <MenuItem
                        key={option.uuid}
                        value={option.uuid}
                        sx={{
                          fontFamily: 'Roboto',
                          fontSize: '16px',
                          fontWeight: 400,
                          lineHeight: '24px',
                          letterSpacing: '0.15px',
                          textAlign: 'left',
                        }}
                      >
                        {option.name}
                      </MenuItem>
                    );
                  })}
                </Field>

                {touched.loginData?.permission &&
                  errors.loginData?.permission && (
                    <Typography
                      color='error'
                      variant='caption'
                      textAlign={'left'}
                    >
                      {errors.loginData?.permission}
                    </Typography>
                  )}
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
              <Field
                name='loginData.password'
                as={PasswordField}
                label='Senha de assinatura digital'
                value={values.loginData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                enableViewPassword={permissionViewPassword}
                error={
                  touched.loginData?.password &&
                  Boolean(errors.loginData?.password)
                }
                helperText={
                  touched.loginData?.password && errors.loginData?.password
                }
              />

              <Field
                name='loginData.confirmPassword'
                as={PasswordField}
                label='Repita a senha de assinatura digital'
                value={values.loginData.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                enableViewPassword={permissionViewPassword}
                error={
                  touched.loginData?.confirmPassword &&
                  Boolean(errors.loginData?.confirmPassword)
                }
                helperText={
                  touched.loginData?.confirmPassword &&
                  errors.loginData?.confirmPassword
                }
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default LoginDataForm;
